import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';

const JobSectorBrowserPlumbingServices = () => {
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [locationRadius, setLocationRadius] = useState('50');

  const plumbingJobs = [
    {
      title: 'Residentiële badkamer renovatie',
      location: 'Amsterdam',
      budget: '€3.500 - €6.000',
      deadline: '3 weken',
      description: 'Complete badkamer renovatie met nieuwe sanitair en leidingwerk',
      serviceType: 'Badkamer installatie',
      pipeMaterial: 'PEX, koper',
      waterPressure: '3-5 bar',
      certification: 'VCA, Gas safety',
      hourlyRate: '€65',
      urgency: 'normal'
    },
    {
      title: 'Commerciële sanitair installatie',
      location: 'Rotterdam',
      budget: '€18.000 - €28.000',
      deadline: '6 weken',
      description: 'Sanitair installatie kantoorpand met 8 toiletgroepen en pantry\'s',
      serviceType: 'Commerciële installatie',
      pipeMaterial: 'RVS, PVC',
      waterPressure: '4-6 bar',
      certification: 'VCA, Legionella preventie',
      hourlyRate: '€75',
      urgency: 'normal'
    },
    {
      title: 'Noodgeval lekkage reparatie',
      location: 'Utrecht',
      budget: '€500 - €1.200',
      deadline: 'Direct',
      description: 'Spoedreparatie waterleiding lekkage in wooncomplex',
      serviceType: 'Nooddienst',
      pipeMaterial: 'Koper, PEX',
      waterPressure: '3-4 bar',
      certification: 'VCA',
      hourlyRate: '€85',
      urgency: 'emergency'
    },
    {
      title: 'Centrale verwarming installatie',
      location: 'Den Haag',
      budget: '€8.000 - €12.000',
      deadline: '4 weken',
      description: 'Installatie CV-systeem met radiatoren en vloerverwarming',
      serviceType: 'Centrale verwarming',
      pipeMaterial: 'Meerlagen, koper',
      waterPressure: '1.5-2.5 bar',
      certification: 'VCA, Gas safety',
      hourlyRate: '€70',
      urgency: 'normal'
    },
    {
      title: 'Waterbehandeling systeem installatie',
      location: 'Eindhoven',
      budget: '€4.000 - €7.000',
      deadline: '2 weken',
      description: 'Installatie waterontharder en filtratiesysteem voor woning',
      serviceType: 'Waterbehandeling',
      pipeMaterial: 'PEX, kunststof',
      waterPressure: '3-5 bar',
      certification: 'Waterbehandeling specialist',
      hourlyRate: '€68',
      urgency: 'normal'
    },
    {
      title: 'Duurzaam rioolsysteem nieuwbouw',
      location: 'Groningen',
      budget: '€12.000 - €18.000',
      deadline: '8 weken',
      description: 'Duurzaam rioolsysteem met regenwater opvang nieuwbouwproject',
      serviceType: 'Drainage & riool',
      pipeMaterial: 'PVC, PP',
      waterPressure: 'N/A',
      certification: 'VCA, Duurzaam bouwen',
      hourlyRate: '€72',
      urgency: 'normal'
    }
  ];

  const certifiedPlumbers = [
    {
      name: 'AquaTech Loodgieters',
      rating: 4.9,
      reviews: 203,
      location: 'Amsterdam',
      specialization: 'Badkamer & Sanitair',
      certifications: ['VCA', 'Gas safety', 'Legionella preventie'],
      experience: '17+ jaar',
      availability: 'Direct beschikbaar',
      hourlyRate: '€70',
      completedProjects: 450,
      emergencyService: true
    },
    {
      name: 'ProPipe Installaties',
      rating: 4.8,
      reviews: 178,
      location: 'Rotterdam',
      specialization: 'Commerciële Projecten',
      certifications: ['VCA', 'Legionella preventie', 'ISO 9001'],
      experience: '15+ jaar',
      availability: 'Beschikbaar over 1 week',
      hourlyRate: '€75',
      completedProjects: 380,
      emergencyService: false
    },
    {
      name: 'Emergency Plumbing 24/7',
      rating: 4.7,
      reviews: 156,
      location: 'Utrecht',
      specialization: 'Nooddiensten',
      certifications: ['VCA', 'Gas safety'],
      experience: '12+ jaar',
      availability: '24/7 beschikbaar',
      hourlyRate: '€85',
      completedProjects: 520,
      emergencyService: true
    },
    {
      name: 'Sustainable Water Solutions',
      rating: 4.9,
      reviews: 189,
      location: 'Eindhoven',
      specialization: 'Duurzame Watersystemen',
      certifications: ['VCA', 'Waterbehandeling', 'Duurzaam bouwen'],
      experience: '14+ jaar',
      availability: 'Direct beschikbaar',
      hourlyRate: '€72',
      completedProjects: 340,
      emergencyService: false
    }
  ];

  const specializations = [
    { value: 'all', label: 'Alle specialisaties' },
    { value: 'bathroom', label: 'Badkamer installaties' },
    { value: 'heating', label: 'Centrale verwarming' },
    { value: 'emergency', label: 'Nooddiensten' },
    { value: 'water-treatment', label: 'Waterbehandeling' },
    { value: 'drainage', label: 'Drainage & riool' },
    { value: 'gas', label: 'Gas fitting' }
  ];

  const getUrgencyColor = (urgency) => {
    const colors = {
      emergency: 'bg-error/10 text-error border-error/20',
      urgent: 'bg-warning/10 text-warning border-warning/20',
      normal: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[urgency] || colors?.normal;
  };

  const getUrgencyLabel = (urgency) => {
    const labels = {
      emergency: 'Spoed',
      urgent: 'Urgent',
      normal: 'Normaal'
    };
    return labels?.[urgency] || 'Normaal';
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <motion.section 
          className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16 md:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Icon name="Droplet" size={32} className="text-blue-700" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                  Loodgieter Werkzaamheden - Vakgebied Browser
                </h1>
              </div>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Gespecialiseerde loodgieter opdrachten en gecertificeerde vakmensen voor sanitair, waterbehandeling, drainage en nooddiensten.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve opdrachten', value: '48', icon: 'Briefcase' },
                { label: 'Gecertificeerde loodgieters', value: '156', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.8', icon: 'Star' },
                { label: 'Voltooide projecten', value: '2.2k+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6" style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-4">Geavanceerde Filters</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Specialisatie</label>
                  <select 
                    value={selectedSpecialization}
                    onChange={(e) => setSelectedSpecialization(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {specializations?.map(spec => (
                      <option key={spec?.value} value={spec?.value}>{spec?.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Locatie Radius</label>
                  <select 
                    value={locationRadius}
                    onChange={(e) => setLocationRadius(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="10">Binnen 10 km</option>
                    <option value="25">Binnen 25 km</option>
                    <option value="50">Binnen 50 km</option>
                    <option value="100">Binnen 100 km</option>
                    <option value="all">Heel Nederland</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Urgentie</label>
                  <select 
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="alle">Alle opdrachten</option>
                    <option value="emergency">Spoed</option>
                    <option value="urgent">Urgent</option>
                    <option value="normal">Normaal</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Section */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">Beschikbare Opdrachten</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Briefcase" size={16} />
                <span>{plumbingJobs?.length} opdrachten gevonden</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {plumbingJobs?.map((job, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-primary transition-all"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{job?.title}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Icon name="MapPin" size={14} />
                        <span>{job?.location}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getUrgencyColor(job?.urgency)}`}>
                      {getUrgencyLabel(job?.urgency)}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{job?.description}</p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Wrench" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.serviceType}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Activity" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.waterPressure}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Package" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.pipeMaterial}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Award" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.certification}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <div className="text-2xl font-bold text-foreground">{job?.budget}</div>
                      <div className="text-xs text-muted-foreground">{job?.hourlyRate}/uur</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-sm font-medium text-foreground">Deadline</div>
                        <div className="text-xs text-muted-foreground">{job?.deadline}</div>
                      </div>
                      <Button size="sm" className="ml-2">
                        Direct Solliciteren
                        <Icon name="ArrowRight" size={14} />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certified Plumbers Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">Gecertificeerde Loodgieters</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>{certifiedPlumbers?.length} vakmensen beschikbaar</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {certifiedPlumbers?.map((plumber, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{plumber?.name}</h3>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-1">
                          <Icon name="Star" size={14} className="text-warning fill-warning" />
                          <span className="text-sm font-medium text-foreground">{plumber?.rating}</span>
                          <span className="text-xs text-muted-foreground">({plumber?.reviews} reviews)</span>
                        </div>
                        {plumber?.emergencyService && (
                          <span className="px-2 py-0.5 bg-error/10 text-error text-xs font-medium rounded-full border border-error/20">
                            24/7 Nooddienst
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span>{plumber?.location}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-sm font-medium text-foreground mb-2">Specialisatie</div>
                    <div className="text-sm text-primary font-medium">{plumber?.specialization}</div>
                  </div>

                  <div className="mb-4">
                    <div className="text-sm font-medium text-foreground mb-2">Certificeringen</div>
                    <div className="flex flex-wrap gap-2">
                      {plumber?.certifications?.map((cert, idx) => (
                        <span key={idx} className="px-2 py-1 bg-success/10 text-success text-xs rounded-full border border-success/20">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Ervaring</div>
                      <div className="text-sm font-medium text-foreground">{plumber?.experience}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Voltooide projecten</div>
                      <div className="text-sm font-medium text-foreground">{plumber?.completedProjects}+</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Uurtarief</div>
                      <div className="text-xl font-bold text-foreground">{plumber?.hourlyRate}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground mb-1">Beschikbaarheid</div>
                        <div className="text-sm font-medium text-success">{plumber?.availability}</div>
                      </div>
                      <Button size="sm" variant="outline" className="ml-2">
                        <Icon name="Phone" size={14} />
                        Contacteren
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Icon name="Droplet" size={48} className="mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Klaar om te starten?
              </h2>
              <p className="text-lg text-white/90 mb-8">
                Vind de perfecte loodgieter opdracht of laat uw vaardigheden zien aan opdrachtgevers
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="outline" className="bg-white text-primary hover:bg-white/90">
                  <Icon name="Search" size={18} />
                  Bekijk alle opdrachten
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Icon name="UserPlus" size={18} />
                  Registreer als loodgieter
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default JobSectorBrowserPlumbingServices;